import type { VoiceCommandResult } from './voiceCommandClient';
import { VOICE_PENDING_TTL, VOICE_STORAGE_SUFFIXES } from '../voice/constants';
import {
  isExpired,
  migrateLegacyVoiceStorage,
  readVoiceKey,
  removeVoiceKey,
  resolveVoiceStorageScope,
  writeVoiceKey,
} from '../voice/voiceStorage';

export interface PendingDocumentFlow {
  intent: string;
  customer_name?: string;
  supplier_name?: string;
  contact_id?: number;
  missing_entity?: string;
  gathering_customer?: boolean;
  gathering_items: boolean;
  suggested_customers?: Array<{ id: number; name: string; index: number }>;
  suggested_suppliers?: Array<{ id: number; name: string; index: number }>;
  items?: Array<{
    description: string;
    quantity: number;
    unit_price: number;
    tax_rate?: number;
  }>;
  createdAt?: number;
  expiresAt?: number;
}

function scopeOrNull() {
  const scope = resolveVoiceStorageScope();
  if (scope) migrateLegacyVoiceStorage(scope);
  return scope;
}

export function storePendingDocumentFlow(result: VoiceCommandResult): void {
  const gatheringCustomer = result.phase === 'gathering_customer'
    || result.data?.phase === 'gathering_customer';

  const gatheringItems = result.phase === 'gathering_items'
    || result.data?.gathering_items === true
    || result.data?.missing_entity?.startsWith('item_')
    || result.data?.missing_entity === 'more_items';

  if (gatheringCustomer) {
    const intent = result.data?.intent
      || result.conversation_state?.original_intent
      || result.conversation_state?.current_intent
      || 'create_quote';

    const createdAt = Date.now();
    const pending: PendingDocumentFlow = {
      intent,
      gathering_customer: true,
      gathering_items: false,
      missing_entity: result.data?.missing_entity || result.nextQuestion || 'customer_name',
      suggested_customers: Array.isArray(result.data?.suggested_customers)
        ? result.data.suggested_customers
        : undefined,
      suggested_suppliers: Array.isArray(result.data?.suggested_suppliers)
        ? result.data.suggested_suppliers
        : undefined,
      createdAt,
      expiresAt: createdAt + VOICE_PENDING_TTL.DEFAULT_MS,
    };

    const scope = scopeOrNull();
    if (!scope) return;
    writeVoiceKey(
      scope,
      VOICE_STORAGE_SUFFIXES.pendingDocument,
      pending,
      VOICE_PENDING_TTL.DEFAULT_MS
    );
    return;
  }

  if (!gatheringItems) return;

  const intent = result.data?.intent
    || result.conversation_state?.original_intent
    || result.conversation_state?.current_intent
    || 'create_quote';

  const createdAt = Date.now();
  const pending: PendingDocumentFlow = {
    intent,
    gathering_items: true,
    missing_entity: result.data?.missing_entity || result.nextQuestion || 'item_description',
    customer_name: typeof result.data?.customer_name === 'string'
      ? result.data.customer_name
      : undefined,
    supplier_name: typeof result.data?.supplier_name === 'string'
      ? result.data.supplier_name
      : undefined,
    contact_id: typeof result.data?.contact_id === 'number' ? result.data.contact_id : undefined,
    items: Array.isArray(result.data?.items) ? result.data.items : [],
    createdAt,
    expiresAt: createdAt + VOICE_PENDING_TTL.DEFAULT_MS,
  };

  if (!pending.customer_name && !pending.supplier_name && result.message) {
    const match = result.message.match(/(?:Great\s*[—-]\s*)?(?:quote|invoice|bill)\s+for\s+(.+?)\.\s/i);
    if (match?.[1]) {
      pending.customer_name = match[1].trim();
    }
  }

  const scope = scopeOrNull();
  if (!scope) return;
  writeVoiceKey(
    scope,
    VOICE_STORAGE_SUFFIXES.pendingDocument,
    pending,
    VOICE_PENDING_TTL.DEFAULT_MS
  );
}

export function getPendingDocumentFlow(): PendingDocumentFlow | null {
  const scope = scopeOrNull();
  if (!scope) return null;
  const pending = readVoiceKey<PendingDocumentFlow>(
    scope,
    VOICE_STORAGE_SUFFIXES.pendingDocument
  );
  if (!pending) return null;
  if (isExpired(pending)) {
    removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.pendingDocument);
    return null;
  }
  return pending;
}

export function clearPendingDocumentFlow(): void {
  const scope = scopeOrNull();
  if (scope) removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.pendingDocument);
  try {
    localStorage.removeItem('voicePendingDocumentFlow');
  } catch {
    /* ignore */
  }
}
